import axios from "../axiosInstance"

export const getIrrCompanies = async () => {
  try {
    const res = await axios.get(`/irr/companies`)
    const { data } = res
    if (!data.success) {
      throw new Error(data.message || "Failed to fetch IRR companies")
    }

    return data.irrCompanies
  } catch (error) {
    console.error("Error fetching IRR companies:", error)
    throw error
  }
}

export const getIrrDeals = async () => {
  try {
    const res = await axios.get(`/irr/deals`)
    const { data } = res

    if (!data.success) {
      throw new Error(data.message || "Failed to fetch IRR deals")
    }

    return data.irrDeals
  } catch (error) {
    console.error("Error fetching IRR deals:", error)
    throw error
  }
}

export const addIrrCashFlow = async (dealId: number, cashFlowDate: string, cashFlow: number, memo: string) => {
  const res = await axios.post(`/irr/cash-flows`, { dealId, cashFlowDate, cashFlow, memo })
  return res.data
}

export const updateIrrCashFlow = async (id: number, cashFlowDate: string, cashFlow: number, memo: string) => {
  const res = await axios.put(`/irr/cash-flows/${id}`, { cashFlowDate, cashFlow, memo })
  return res.data
}

export const deleteIrrCashFlow = async (id: number) => {
  const res = await axios.delete(`/irr/cash-flows/${id}`)
  return res.data
}

export const getFundDashboard = async () => {
  const res = await axios.get(`/irr/fund-dashboard`)
  if (!res.data.success) throw new Error("Failed to fetch fund dashboard")
  return res.data.funds
}

export const updateFundAccounting = async (
  fundId: number,
  fields: {
    totalPaidIn?: number | null
    totalDistributions?: number | null
    cashAvailable?: number | null
    prepaidExpenses?: number | null
    prepaidLpCommitments?: number | null
    undistributedRealizations?: number | null
    lastDistributionDate?: string | null
    outstandingPayables?: number | null
  }
) => {
  const res = await axios.put(`/irr/funds/${fundId}/accounting`, fields)
  if (!res.data.success) throw new Error("Failed to update fund accounting")
  return res.data
}

// ─── Fund Capital Call API ──────────────────────────────────────

export const listFundCapitalCalls = async (fundId: number) => {
  const res = await axios.get(`/irr/funds/${fundId}/capital-calls`)
  if (!res.data.success) throw new Error("Failed to list capital calls")
  return res.data.capitalCalls
}

export const addFundCapitalCall = async (fundId: number, callDate: string, amount: number, memo?: string) => {
  const res = await axios.post(`/irr/funds/${fundId}/capital-calls`, { callDate, amount, memo })
  if (!res.data.success) throw new Error("Failed to add capital call")
  return res.data.capitalCall
}

export const updateFundCapitalCall = async (id: number, data: { callDate?: string; amount?: number; memo?: string | null }) => {
  const res = await axios.put(`/irr/fund-capital-calls/${id}`, data)
  if (!res.data.success) throw new Error("Failed to update capital call")
  return res.data.capitalCall
}

export const deleteFundCapitalCall = async (id: number) => {
  const res = await axios.delete(`/irr/fund-capital-calls/${id}`)
  if (!res.data.success) throw new Error("Failed to delete capital call")
  return res.data
}

// ─── Fund Distribution API ─────────────────────────────────────

export const listFundDistributions = async (fundId: number) => {
  const res = await axios.get(`/irr/funds/${fundId}/distributions`)
  if (!res.data.success) throw new Error("Failed to list distributions")
  return res.data.distributions
}

export const addFundDistribution = async (fundId: number, distributionDate: string, amount: number, memo?: string) => {
  const res = await axios.post(`/irr/funds/${fundId}/distributions`, { distributionDate, amount, memo })
  if (!res.data.success) throw new Error("Failed to add distribution")
  return res.data.distribution
}

export const updateFundDistribution = async (id: number, data: { distributionDate?: string; amount?: number; memo?: string | null }) => {
  const res = await axios.put(`/irr/fund-distributions/${id}`, data)
  if (!res.data.success) throw new Error("Failed to update distribution")
  return res.data.distribution
}

export const deleteFundDistribution = async (id: number) => {
  const res = await axios.delete(`/irr/fund-distributions/${id}`)
  if (!res.data.success) throw new Error("Failed to delete distribution")
  return res.data
}

export const recomputeIrrMetrics = async (): Promise<{ deals: number; companies: number }> => {
  const res = await axios.post(`/irr/recompute`)
  if (!res.data.success) throw new Error("Recompute failed")
  return { deals: res.data.deals, companies: res.data.companies }
}

export interface IrrDealManualFields {
  originalClosingDate: string
  inceptionDate?: string
  creditScore?: number | null
  finalManagerId?: number | null
  backupManagerId?: number | null
  transactionEmployeeCount?: number | null
  referralFeeOutflow?: number | null
  industry?: string | null
  geography?: string | null
}

export const registerDealForIrr = async (dealId: number, fields: IrrDealManualFields) => {
  const res = await axios.post(`/irr/deals/${dealId}/register`, fields)
  if (!res.data.success) throw new Error("Failed to register deal for IRR")
  return res.data.irrDeal
}

export const downloadNewCompaniesXlsx = async (quarter?: number, year?: number) => {
  const params = quarter && year ? `?quarter=${quarter}&year=${year}` : "";
  const res = await axios.get(`/irr/new-companies/xlsx${params}`, { responseType: "blob" })

  // If the backend returned JSON (no new companies), the content-type will be application/json
  const contentType = res.headers["content-type"] || ""
  if (contentType.includes("application/json")) {
    const text = await (res.data as Blob).text()
    const json = JSON.parse(text)
    throw new Error(json.message || "No data available")
  }

  const url = window.URL.createObjectURL(new Blob([res.data]))
  const link = document.createElement("a")
  link.href = url
  const disposition = res.headers["content-disposition"]
  const filename = disposition?.match(/filename="(.+)"/)?.[1] ?? "new-companies.xlsx"
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}